/**
 * Human Agent API
 * 人工客服接口
 *
 * API 路径:
 * - /conversations/{userId}/human/join - 人工加入请求
 * - /conversations/{userId}/human/summary - 获取对话汇总
 * - /conversations/{userId}/human/message - 人工客服发送消息
 * - /tickets/conversation/{conversationId} - 获取 ticket 状态
 */

import { apiClient } from '../../core/api';
import type {
  ConversationAISummary,
  ConversationAISummaryResponse,
  JoinConversationResponse,
  UIMessage,
} from '../../../types/customers';

// ==================== 时间格式化 ====================

const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isYesterday =
    new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();

  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (isToday) {
    return timeString;
  } else if (isYesterday) {
    return `Yesterday at ${timeString}`;
  } else {
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `${formattedDate} at ${timeString}`;
  }
};

// ==================== 类型定义 ====================

// Ticket 状态类型
// init: 等待加入
// open: 开放中，可加入
// inProgress: 进行中
// closed: 已关闭
export interface TicketInfo {
  id: string;
  clientId: string;
  status: 'init' | 'open' | 'inProgress' | 'closed';
  conversationId: string;
  summary: string;
  createdAt: string;
  updatedAt: string;
  client: {
    id: string;
    userId: string;
  };
  supportType: string;
}

// 发送消息 API 响应类型
interface SendMessageResponse {
  id: string;
  content: string;
  sender: string;
  agentName: string;
  createdAt: string;
}

// ==================== API 函数 ====================

/**
 * 获取对话摘要
 * GET /conversations/{userId}/human/summary?conversationId={conversationId}
 */
export const getConversationSummary = async (
  userId: string,
  conversationId: string
): Promise<ConversationAISummary> => {
  try {
    const response = await apiClient.chatbotGet<ConversationAISummaryResponse>(
      `/conversations/${userId}/human/summary?conversationId=${conversationId}`
    );

    // Transform API response to UI format
    return {
      chatSummary: response.data.summary,
      mainIssues: response.data.main_issues,
      recommendations: response.data.recommendations,
    };
  } catch (error) {
    console.error('Failed to get conversation summary:', error);
    throw error;
  }
};

/**
 * 人工加入对话
 * POST /conversations/{userId}/human/join
 */
export const joinConversation = async (
  userId: string,
  conversationId: string,
  agentName: string,
  anonymousId: string
): Promise<JoinConversationResponse> => {
  try {
    const response = await apiClient.chatbotPost<JoinConversationResponse>(
      `/conversations/${userId}/human/join`,
      { conversationId, agentName, anonymousId }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to join conversation:', error);
    throw error;
  }
};

/**
 * 发送人工消息
 * POST /conversations/{userId}/human/message
 */
export const sendHumanMessage = async (
  userId: string,
  conversationId: string,
  content: string,
  agentName: string,
  anonymousId: string
): Promise<UIMessage> => {
  try {
    const response = await apiClient.chatbotPost<SendMessageResponse>(
      `/conversations/${userId}/human/message`,
      { conversationId, content, agentName, anonymousId }
    );

    console.log('Send message response:', response.data);

    // 使用请求的 content 作为备用，如果 API 没有返回 content
    const messageContent = response.data.content || content;
    // 使用当前时间作为备用，如果 API 没有返回 createdAt
    const rawCreatedAt = response.data.createdAt || new Date().toISOString();
    const timestamp = formatTimestamp(rawCreatedAt);

    return {
      id: response.data.id || `msg-${Date.now()}`,
      content: messageContent,
      sender: 'agent',
      timestamp,
      rawCreatedAt,
      agentName: response.data.agentName || agentName,
    };
  } catch (error) {
    console.error('Failed to send human message:', error);
    throw error;
  }
};

/**
 * 离开对话
 * POST /conversations/{userId}/human/leave
 */
export const leaveConversation = async (
  userId: string,
  conversationId: string,
  agentName: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.chatbotPost<{ success: boolean; message: string }>(
      `/conversations/${userId}/human/leave`,
      { conversationId, agentName }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to leave conversation:', error);
    throw error;
  }
};

/**
 * 获取对话的 ticket 状态
 * GET /tickets/conversation/{conversationId}
 * 根据 createdAt 排序，只返回最新的一条 ticket
 */
export const getTicketStatus = async (
  conversationId: string
): Promise<TicketInfo[]> => {
  try {
    const response = await apiClient.chatbotGet<TicketInfo[]>(
      `/tickets/conversation/${conversationId}`
    );
    // 根据 createdAt 排序，取最新的一条
    if (response.data.length > 0) {
      const sorted = [...response.data].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      return [sorted[sorted.length - 1]];
    }
    return [];
  } catch (error) {
    console.error('Failed to get ticket status:', error);
    throw error;
  }
};

/**
 * 哪些对话已经要求转人工、但还没有人接。
 *
 * <p>后端只返回"还在等"的那些（工单停在 init / open；inProgress 表示已经有人接管，不算），
 * 而且是该商家的全量——等待中的对话本来就少，所以不用把当前页的 id 列表塞进请求。见
 * chatbot-api #1836。
 *
 * <p>响应约定始终带列表，空也是空列表；这里仍然兜一层，因为"标注不显示"远好过因为一个
 * 锦上添花的信号把整页搞崩。
 */
export const getHandoffPending = async (userId: string): Promise<string[]> => {
  try {
    const response = await apiClient.chatbotGet<{ conversationIds?: string[] }>(
      HUMAN_AGENT_API_PATHS.handoffPending(userId)
    );
    return response.data?.conversationIds ?? [];
  } catch (error) {
    console.error('Failed to get handoff-pending conversations:', error);
    throw error;
  }
};

/**
 * CS 2.0 需求 13：转人工后团队还没回复的会话数，其中紧急的有几个。
 *
 * <p>左侧导航 Support / Anna 的红点、首页 Customer Support 卡读它。后端和店长简报、Anna Chat 的
 * `filter=handoff_unreplied`、Leads 标识读同一个判定（`ConversationHandoffRepository.UNREPLIED`），
 * 几处数字一致。
 *
 * <p>和 {@link getHandoffPending} 一样：读不到就抛，由调用方决定退回什么——红点读不到时不能
 * 画成「0 个在等」以外的任何东西。
 */
export interface HandoffSummary {
  unreplied: number;
  urgent: number;
}

export const getHandoffSummary = async (userId: string): Promise<HandoffSummary> => {
  const response = await apiClient.chatbotGet<Partial<HandoffSummary> & { errorCode?: unknown }>(
    HUMAN_AGENT_API_PATHS.handoffSummary(userId)
  );
  const body = response.data;
  // 非 401 错误以 200 + {errorCode, errorMessage} 返回（见 core/api.ts）——当成失败，不当成 0。
  if (!body || body.errorCode) {
    throw new Error('handoff summary unavailable');
  }
  const count = (value: unknown) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  return { unreplied: count(body.unreplied), urgent: count(body.urgent) };
};

// ==================== API 路径常量 ====================

export const HUMAN_AGENT_API_PATHS = {
  summary: (userId: string) => `/conversations/${userId}/human/summary`,
  join: (userId: string) => `/conversations/${userId}/human/join`,
  message: (userId: string) => `/conversations/${userId}/human/message`,
  leave: (userId: string) => `/conversations/${userId}/human/leave`,
  ticketStatus: (conversationId: string) => `/tickets/conversation/${conversationId}`,
  handoffPending: (userId: string) => `/conversations/${userId}/human/pending`,
  handoffSummary: (userId: string) => `/conversations/${userId}/handoff-summary`,
};
